import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDT, fmtDate, daysLeft } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, MetricCard, ProgressBar, StatusBadge, Loading } from "@/components/Shared";
import { ShoppingCart, ClipboardList, Wallet, Sparkles, ArrowRight, Clock, Newspaper } from "lucide-react";

export default function PharmacistDashboard() {
  const { user } = useAuth();
  const [ops, setOps] = useState(null);
  const [orders, setOrders] = useState([]);
  const [recos, setRecos] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    api.get("/operations", { params: { status: "open" } }).then((r) => setOps(r.data)).catch(() => setOps([]));
    api.get("/participations/me").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/recommendations").then((r) => setRecos(r.data)).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 4))).catch(() => {});
  }, []);

  const spent = orders.reduce((s, o) => s + (o.total_amount || 0), 0);

  return (
    <div>
      <PageHeader title={`Bonjour, ${user?.name?.split(" ").slice(-1)[0] || ""} 👋`} subtitle="Voici les opportunités d'achat du moment." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard testId="metric-open-ops" label="Opérations ouvertes" value={ops?.filter((o) => o.status === "open").length ?? "—"} icon={ShoppingCart} tone="indigo" />
        <MetricCard testId="metric-my-orders" label="Mes participations" value={orders.length} icon={ClipboardList} tone="sky" />
        <MetricCard testId="metric-spent" label="Total engagé" value={fmtDT(spent)} icon={Wallet} tone="emerald" />
        <MetricCard testId="metric-recos" label="Recommandations" value={recos.length} icon={Sparkles} tone="amber" />
      </div>

      {/* AI recommendations */}
      {recos.length > 0 && (
        <div className="mb-8 rounded-xl border-2 border-indigo-200 bg-indigo-50/40 p-5" data-testid="reco-section">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <h3 className="font-bold text-slate-900">Recommandé pour vous</h3>
            <span className="text-xs font-semibold text-indigo-600 bg-white border border-indigo-200 px-2 py-0.5 rounded-full">IA</span>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {recos.slice(0, 3).map((op) => (
              <Link key={op.id} to={`/operations/${op.id}`} data-testid={`reco-${op.id}`} className="block bg-white rounded-lg border border-indigo-100 p-4 hover:-translate-y-0.5 transition-transform duration-200">
                <div className="text-sm font-bold text-slate-900 line-clamp-1">{op.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">{op.supplier_name}</div>
                <p className="mt-2 text-xs font-medium text-indigo-600 italic flex items-start gap-1"><Sparkles className="h-3 w-3 mt-0.5 shrink-0" />{op.reason}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-900">Opérations en cours</h3>
            <Link to="/operations" className="text-sm font-semibold text-indigo-600 hover:underline flex items-center gap-1">Tout voir <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          {ops === null ? <Loading /> : (
            <div className="space-y-4">
              {ops.filter((o) => o.status === "open").slice(0, 4).map((op) => {
                const dl = daysLeft(op.close_date);
                return (
                  <Link key={op.id} to={`/operations/${op.id}`} data-testid={`dash-op-${op.id}`} className="block bg-white rounded-xl border border-slate-200 p-5 hover:border-indigo-200 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-bold text-slate-900">{op.name}</div>
                        <div className="text-sm text-slate-400">{op.supplier_name}</div>
                      </div>
                      <StatusBadge status={op.status} />
                    </div>
                    <div className="mt-4"><ProgressBar current={op.committed_amount} target={op.objective_amount} /></div>
                    <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-amber-600"><Clock className="h-3.5 w-3.5" />{dl > 0 ? `Clôture dans ${dl} j` : "Clôturée"}</div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4"><Newspaper className="h-5 w-5 text-slate-400" /><h3 className="font-bold text-slate-900">Veille professionnelle</h3></div>
          <div className="space-y-3">
            {news.map((n) => (
              <Link key={n.id} to="/news" className="block bg-white rounded-lg border border-slate-200 p-4 hover:border-indigo-200 transition-colors duration-200">
                <div className="text-xs font-semibold text-indigo-600 uppercase">{n.type}</div>
                <div className="mt-1 text-sm font-bold text-slate-900 line-clamp-2">{n.title}</div>
                <div className="mt-1.5 text-xs text-slate-400">{n.organism} · {fmtDate(n.date)}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
